import React, { useEffect, useState, useContext } from 'react';
import { Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { Form } from 'react-bootstrap';

import WhitePad from '../../../components/common/WhitePad/WhitePad';
import FormRow from '../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../components/common/forms/TextInput/TextInputComponent';
import PrimaryButton from '../../../components/common/forms/Button/PrimaryButton';
import TitleComponent from '../../../components/common/forms/Title/TitleComponent';
import { API_URL, ROLES } from '~/src/utils/constants';
import { autocompleteToList, constantsToAutocomplete, grantedForRole } from '~/src/utils/misc';
import MyContext from '~/src/services/context';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';
import IntegrationsApi from '~/src/services/api/IntegrationsApi';
import { validationSchema } from './validationSchema';
import BorderlessButton from '../../../components/common/forms/Button/BorderlessButton';
import AutocompleteStaticComponent from '~/src/components/common/forms/Autcomplete/AutocompleteStaticComponent';
import { IntegrationGetDto } from '~/src/types/integrations/IntegrationGetDto';
import { IntegrationEditDto } from '~/src/types/integrations/IntegrationEditDto';
import { IntegrationCredentialEditDto } from '~/src/types/integrations/IntegrationCredentialEditDto';
import AutocompletePaginatedComponent from '~/src/components/common/forms/Autcomplete/AutocomplitePaginatedComponent';
import RolesDependent from '~/src/components/common/RolesDependent';
import { Colors } from '~/src/scss/common/style';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { ActivateIntegrationResponseDto } from '~/src/types/ActivateIntegrationResponseDto';
import AutocompleteComponent from '~/src/components/common/forms/Autcomplete/AutocompleteComponent';
import UserLocalService from '~/src/services/logic/userService';
import { INTEGRATION_STATUS, INTEGRATION_TYPE } from '~/src/utils/integrationsConstants';

export default function IntegrationEdit(): JSX.Element {
  const { id } = useParams();
  const context = useContext(MyContext);
  const [integrationTypeUrl, setIntegrationTypeUrl] = useState(`${API_URL}/integrations/integration-types`);
  const [itemData, setItemData] = useState({
    integration: null,
    dataArrived: false,
  });
  const [credentialsSchema, setCredentialsSchema] = useState<string[]>([]);
  const [initialCredentialsFields, setInitialCredentialsFields] = useState<Record<string, string>>({});
  const [localStatus, setLocalStatus] = useState<number>(itemData?.integration?.status ?? INTEGRATION_STATUS.INACTIVE.value);
  const [activateModal, setActivateModal] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const history = useHistory();

  const saveItem = async (values, setSubmitting) => {
    setSubmitting(true);
    let result = null;

    const integrationCredentialEditDto: IntegrationCredentialEditDto = {
      apiUrl: values.apiUrl,
      accessCredentials: values.accessCredentials,
    };

    const integrationEditDto: IntegrationEditDto = {
      svilaClientId: values.svilaClientId,
      name: values.integrationName,
      status: values.status,
      integrationType: values.integrationType,
      integrationCredential: integrationCredentialEditDto
    };

    try {
      if (id) {
        result = await IntegrationsApi.update(id, integrationEditDto);
      } else {
        result = await IntegrationsApi.create(integrationEditDto);
      }

      if (result.status === StatusCodes.OK) {
        const integrationTypeLabel =
          Object.values(INTEGRATION_TYPE).find(t => t.value === Number(values.integrationType))?.label;

        if (integrationTypeLabel) {
          const userFromDb = UserLocalService.getUser();
          const updatedUser = updateActiveIntegrations(userFromDb, integrationTypeLabel, values.status);
          await UserLocalService.setUser(updatedUser);
          if (context.setUser) context.setUser(updatedUser);
        }

        history.push('/integrations');
      } else {
        setSubmitting(false);
      }
    } catch (error) {
      setSubmitting(false);
    }
  };

  function updateActiveIntegrations(user, integrationLabel, status) {
    let activeIntegrations = Array.isArray(user.activeIntegrations) ? [...user.activeIntegrations] : [];
    if (status === INTEGRATION_STATUS.ACTIVE.value) {
      if (!activeIntegrations.includes(integrationLabel)) {
        activeIntegrations.push(integrationLabel);
      }
    } else if (status === INTEGRATION_STATUS.INACTIVE.value) {
      activeIntegrations = activeIntegrations.filter(label => label !== integrationLabel);
    }
    return { ...user, activeIntegrations };
  }

  const deleteItem = async () => {
    const result = await IntegrationsApi.delete(id);
    if (result.status === StatusCodes.OK) {
      history.push('/integrations');
    }
  };

  const getItem = async () => {
    const result = await IntegrationsApi.getById(id);

    if (result.status === StatusCodes.OK) {
      setItemData({
        integration: result.data,
        dataArrived: true,
      });
    }
  };

  const fetchAndSetCredentialsSchema = async (integrationType: number, accessCredentials?: string) => {
    const result = await IntegrationsApi.getSchema(integrationType);

    if (result.status === StatusCodes.OK) {
      setCredentialsSchema(result.data);

      if (accessCredentials) {
        const parsed: Record<string, string> = {};
        accessCredentials.split(';').forEach(pair => {
          const [key, value] = pair.split('=');

          if (key) parsed[key] = value ?? '';
        });

        const filledFields = result.data.reduce((acc, key) => {
          acc[key] = parsed[key] || '';

          return acc;
        }, {} as Record<string, string>);

        setInitialCredentialsFields(filledFields);
      } else {
        const emptyFields = result.data.reduce((acc, key) => {
          acc[key] = '';

          return acc;
        }, {} as Record<string, string>);

        setInitialCredentialsFields(emptyFields);
      }
    }
  };

  const handleActivate = async (values: any, setFieldValue: any) => {
    const integrationCredential: IntegrationCredentialEditDto = {
      apiUrl: values.apiUrl,
      accessCredentials: Object.entries(values.credentialsFields)
        .map(([key, val]) => `${key}=${val}`)
        .join(';'),
    };
    const result = await IntegrationsApi.activate(integrationCredential, values.integrationType);

    if (result && result.data) {
      setActivateModal({ open: true, message: result.data.message });

      if (result.data.isSuccess) {
        setLocalStatus(INTEGRATION_STATUS.ACTIVE.value);
        setFieldValue('status', INTEGRATION_STATUS.ACTIVE.value);

      } else {
        setLocalStatus(INTEGRATION_STATUS.INACTIVE.value);
        setFieldValue('status', INTEGRATION_STATUS.INACTIVE.value);
      }
    }
  };

  const isActivateEnabled = (values: any) => {
    if (!values.apiUrl || !values.apiUrl.trim()) return false;

    if (!credentialsSchema.length) return false;
    return credentialsSchema.every((field) => values.credentialsFields?.[field] && values.credentialsFields[field].trim());
  };

  useEffect(() => {
    if (id) {
      getItem(id);
    }
  }, []);

  useEffect(() => {
    if (id && itemData.integration) {
      fetchAndSetCredentialsSchema(
        itemData?.integration?.integrationType,
        itemData?.integration?.integrationCredential?.accessCredentials
      );
    }
  }, [itemData?.integration]);

  useEffect(() => {
    if (itemData?.integration?.status !== undefined) {
      setLocalStatus(itemData.integration.status);
    }
  }, [itemData?.integration?.status]);

  return (
    <>
      <WhitePad>
        <TitleComponent title={`${id ? 'Edit ' : 'Create '}Integration`} />

        <FormWrapper>
          {id && itemData.dataArrived === false ? null : (
            <Formik
              enableReinitialize
              initialValues={{
                integrationName: itemData?.integration?.name || '',
                status: itemData?.integration?.status ?? INTEGRATION_STATUS.INACTIVE.value,
                integrationType: itemData?.integration?.integrationType ?? INTEGRATION_TYPE.UNKNOWN_TYPE.value,
                svilaClientId: itemData?.integration?.svilaClient?.id || '',
                apiUrl: itemData?.integration?.integrationCredential?.apiUrl || '',
                accessCredentials: itemData?.integration?.integrationCredential?.accessCredentials || '',
                credentialsFields: initialCredentialsFields,
              }}
              validationSchema={validationSchema}
              onSubmit={(values, { setSubmitting }) => {
                let accessCredentials = '';
                if (values.credentialsFields) {
                  accessCredentials = Object.entries(values.credentialsFields)
                    .map(([key, val]) => `${key}=${val}`)
                    .join(';');
                }
                return saveItem({ ...values, accessCredentials }, setSubmitting);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                errors,
                setFieldValue,
                touched,
              }) => {
                useEffect(() => {
                  if (id && values.svilaClientId) {
                    setIntegrationTypeUrl(`${API_URL}/integrations/integration-types/${values.svilaClientId}/${id}`);
                  } else if (values.svilaClientId) {
                    setIntegrationTypeUrl(`${API_URL}/integrations/integration-types/${values.svilaClientId}`);
                  } else {
                    setIntegrationTypeUrl(`${API_URL}/integrations/integration-types`);
                  }
                }, [id, values.svilaClientId]);

                return (
                  <form onSubmit={handleSubmit} autoComplete="off">
                    <div>
                      <FormRow>
                        <TextInputComponent
                          name="integrationName"
                          label="Integration Name"
                          value={values.integrationName}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          cols={12}
                          errors={errors}
                        />
                      </FormRow>
                    </div>
                    <FormRow>
                      <div style={{ width: '100%' }}>
                        <label style={{
                          fontWeight: 700, padding: '0px 16px', display: 'block', marginBottom: 4, fontSize: 15, color: Colors.darkBrown
                        }}>
                          Integration Status
                        </label>
                        <div
                          style={{
                            padding: '10px 16px',
                            background: '#FBFBFB',
                            borderRadius: 6,
                            fontWeight: 400,
                            fontSize: 15,
                            border: '1px solidrgb(219, 218, 218)',
                            boxShadow: '0 1px 4px #eeeeee',
                            display: 'block',
                            minWidth: 220,
                            maxWidth: 500,
                            letterSpacing: 0.5,
                            height: 48,
                            lineHeight: '28px',
                            alignItems: 'center',
                            borderColor: '#eeeeee',
                          }}
                        >
                          {
                            Object.values(INTEGRATION_STATUS).find(s => s.value === localStatus)?.label
                            || localStatus
                          }
                        </div>
                      </div>
                    </FormRow>

                    <RolesDependent
                      roles={[
                        ROLES.ADMIN,
                        ROLES.SUPER_ADMIN
                      ]}>
                      <FormRow>
                        <AutocompletePaginatedComponent
                          url={`${API_URL}/svila-clients/get-all`}
                          name="svilaClientId"
                          initialValue={values.svilaClientId}
                          onChange={(name, value) => {
                            setFieldValue(name, value);
                          }}
                          getOptionLabel={(option) => `${option.name}`}
                          errorMessage={errors.svilaClientId}
                          cols={12}
                          label="Svila Client"
                        />
                      </FormRow>
                    </RolesDependent>

                    <FormRow>
                      <AutocompleteComponent
                        key={integrationTypeUrl}
                        url={integrationTypeUrl}
                        name="integrationType"
                        initialValue={values.integrationType}
                        onChange={async (name, value) => {
                          const schemaResult = await IntegrationsApi.getSchema(value);
                          if (schemaResult.status === StatusCodes.OK) {
                            const emptyFields = schemaResult.data.reduce((acc, key) => {
                              acc[key] = '';
                              return acc;
                            }, {});
                            setFieldValue('credentialsFields', emptyFields);
                            setCredentialsSchema(schemaResult.data);

                          }
                          setFieldValue(name, value);
                        }}
                        getOptionLabel={(option) => option?.name}
                        errorMessage={errors.integrationType}
                        label="Integration Type"
                        cols={12}
                      />
                    </FormRow>

                    <FormRow>
                      <TextInputComponent
                        name="apiUrl"
                        label="API URL"
                        value={values.apiUrl}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          handleChange(e);
                          if (localStatus === INTEGRATION_STATUS.ACTIVE.value) {
                            setLocalStatus(INTEGRATION_STATUS.INACTIVE.value);
                            setFieldValue('status', INTEGRATION_STATUS.INACTIVE.value);
                          }
                        }}
                        cols={12}
                        errors={errors}
                        disabled={
                          Number(values.integrationType) === Number(INTEGRATION_TYPE.UNKNOWN_TYPE.value) ||
                          values.integrationType === null ||
                          values.integrationType === undefined ||
                          (values.integrationType === INTEGRATION_TYPE.UNKNOWN_TYPE.value)
                        }
                      />
                    </FormRow>

                    {credentialsSchema.length > 0 && (
                      <FormRow>
                        {credentialsSchema.map((field) => (
                          <TextInputComponent
                            key={field}
                            name={`credentialsFields.${field}`}
                            label={field}
                            value={values.credentialsFields?.[field] || ''}
                            onBlur={handleBlur}
                            onChange={(e) => {
                              handleChange(e);
                              if (localStatus === INTEGRATION_STATUS.ACTIVE.value) {
                                setLocalStatus(INTEGRATION_STATUS.INACTIVE.value);
                                setFieldValue('status', INTEGRATION_STATUS.INACTIVE.value);
                              }
                            }}
                            cols={12}
                            errors={errors.credentialsFields?.[field]}
                            required
                          />
                        ))}
                      </FormRow>
                    )}

                    <br />
                    <FormRow>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <PrimaryButton
                          variant="primary"
                          isSubmitting={isSubmitting}
                          type="submit"
                          title="Save"
                          disabled={
                            isSubmitting ||
                            !values.integrationName ||
                            !values.svilaClientId ||
                            !values.integrationType ||
                            !values.apiUrl ||
                            (values.integrationType === INTEGRATION_TYPE.UNKNOWN_TYPE.value)
                          }
                        />
                        <PrimaryButton
                          variant="success"
                          type="button"
                          title="Activate"
                          disabled={!isActivateEnabled(values) || localStatus === INTEGRATION_STATUS.ACTIVE.value}
                          onClick={() => handleActivate(values, setFieldValue)}
                          style={{ marginLeft: 16 }}
                        />
                        {id && (
                          <BorderlessButton
                            variant="danger"
                            title="Delete"
                            onClick={deleteItem}
                            style={{ marginLeft: '30px' }}
                          />
                        )}

                      </div>
                    </FormRow>
                  </form>
                )
              }}
            </Formik>
          )}
        </FormWrapper>
      </WhitePad>

      <Modal
        show={activateModal.open}
        onHide={() => setActivateModal({ open: false, message: '' })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Activation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activateModal.message}
        </Modal.Body>
        <Modal.Footer>
          <PrimaryButton title="Ok" variant="primary" onClick={() => setActivateModal({ open: false, message: '' })} />
        </Modal.Footer>
      </Modal>
    </>
  );
}
